import React, { useState, useEffect, useCallback } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { ArrowRight, ArrowLeft, Check, Plus, Minus } from 'lucide-react'
import MarketingNav from './MarketingNav'
import MediaSlot from './MediaSlot'
import SiteFooter from './SiteFooter'
import './marketing-dark.css'
import { slugFor, clusterFor } from './marketingMeta'
import { CATALOG, byKey } from '../lib/serviceCatalog'
import { getSession } from '../lib/session'
import { cartCount } from '../lib/cart'

/**
 * UseCasePage — the public /use-cases/:slug detail page for ai.reright.io.
 *
 * A use-case is an honest BUYER SCENARIO mapped to one real ReRight service. This
 * page REUSES <ProductPage>'s exact dark `.mkt` template (hero → whatItIs →
 * DATA SOURCES → howItWorks → the honest boundaries → useCases → security → faq →
 * related), and is fully HEADLESS: all copy/media come from
 * GET /api/v1/site/usecase/:slug (same `{ product }` envelope as products), so the
 * admin CMS + Marketing agent edit it exactly like a product page.
 *
 * The ONE difference from a product page: there is NO price/cart. A use-case has no
 * SKU of its own — its primary CTA sends the buyer to the MAPPED product page
 * (/products/<service>) to actually switch the service on.
 *
 * Never crashes: a 404 or failed fetch shows a graceful not-found state inside the
 * shared shell (<MarketingNav>/<MediaSlot>/<SiteFooter>).
 */

// Fallback product catalog for the nav + product `related` lookups (icons + price).
const DEFAULT_SERVICES = CATALOG.map((s) => ({
  key: s.key,
  slug: slugFor(s.key),
  cluster: clusterFor(s.key),
  keyword: s.name,
  name: s.name,
  short: s.short,
  price: s.price,
  icon: s.icon,
}))

const titleCase = (slug) =>
  String(slug || '')
    .split('-')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : ''))
    .join(' ')

export default function UseCasePage() {
  const { slug } = useParams()
  const navigate = useNavigate()
  const authed = !!getSession()?.apiKey

  const [uc, setUc] = useState(null)
  const [status, setStatus] = useState('loading') // 'loading' | 'ok' | 'notfound'
  const [services, setServices] = useState(DEFAULT_SERVICES)
  const [usecases, setUsecases] = useState([])
  const [seatCount, setSeatCount] = useState(cartCount())

  // Live cart badge sync (shared nav shell — the badge, not a use-case action).
  useEffect(() => {
    const sync = () => setSeatCount(cartCount())
    sync()
    window.addEventListener('reright-cart', sync)
    return () => window.removeEventListener('reright-cart', sync)
  }, [])

  // Product catalog for the nav mega-dropdown + product `related` lookups.
  useEffect(() => {
    let alive = true
    axios.get('/api/v1/site/catalog')
      .then(({ data }) => {
        if (!alive || !data || !Array.isArray(data.services) || !data.services.length) return
        setServices(data.services.map((s) => ({ ...s, icon: byKey(s.key)?.icon })))
      })
      .catch(() => { /* keep defaults */ })
    return () => { alive = false }
  }, [])

  // Use-case catalog — resolves sibling use-case `related` links (name + short).
  useEffect(() => {
    let alive = true
    axios.get('/api/v1/site/usecases')
      .then(({ data }) => {
        if (!alive || !data || !Array.isArray(data.usecases)) return
        setUsecases(data.usecases)
      })
      .catch(() => { /* keep empty — related still resolves products */ })
    return () => { alive = false }
  }, [])

  // The use-case itself. 404 / failure → graceful not-found (never a crash).
  useEffect(() => {
    let alive = true
    setStatus('loading')
    setUc(null)
    window.scrollTo(0, 0)
    axios.get(`/api/v1/site/usecase/${encodeURIComponent(slug)}`)
      .then(({ data }) => {
        if (!alive) return
        if (data && data.product) { setUc(data.product); setStatus('ok') }
        else setStatus('notfound')
      })
      .catch(() => { if (alive) setStatus('notfound') })
    return () => { alive = false }
  }, [slug])

  // SEO — headless & per-page: unique title + meta description, a page-specific
  // canonical, Open Graph / Twitter cards, and JSON-LD structured data
  // (BreadcrumbList + Service + FAQPage). Everything is read from the fetched
  // use-case (never hardcoded) and fully guarded so a missing field can never
  // crash the page. All tags are cleaned up / reset on navigation so each route
  // owns its own <head>.
  useEffect(() => {
    if (status !== 'ok' || !uc) return
    captureSeoDefaults()

    const kw = uc.keyword || uc.h1 || 'Use case'
    const label = uc.keyword || titleCase(slug)
    const canonicalUrl = `${SITE_ORIGIN}/use-cases/${slug}`
    const pageTitle = uc.title || `${kw} | ReRight`
    const desc = uc.description || uc.intro || ''

    document.title = pageTitle
    setMetaDescription(desc)
    setCanonical(canonicalUrl)

    // Open Graph + Twitter — per page (overrides the homepage defaults).
    setMetaByProperty('og:type', 'website')
    setMetaByProperty('og:title', pageTitle)
    setMetaByProperty('og:description', desc)
    setMetaByProperty('og:url', canonicalUrl)
    setMetaByName('twitter:card', 'summary_large_image')
    setMetaByName('twitter:title', pageTitle)
    setMetaByName('twitter:description', desc)

    // JSON-LD — honest facts only (breadcrumbs, a Service block, real FAQs). No
    // price/cart on a use-case, and no fabricated ratings or reviews.
    try {
      setJsonLd('breadcrumb', {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE_ORIGIN}/` },
          { '@type': 'ListItem', position: 2, name: 'Use cases', item: `${SITE_ORIGIN}/use-cases` },
          { '@type': 'ListItem', position: 3, name: label, item: canonicalUrl },
        ],
      })

      const svc = {
        '@context': 'https://schema.org',
        '@type': 'Service',
        name: uc.h1 || label,
        url: canonicalUrl,
        provider: { '@type': 'Organization', name: 'ReRight', url: SITE_ORIGIN },
      }
      if (desc) svc.description = desc
      setJsonLd('service', svc)

      // FAQPage ONLY from the visible, populated faq array — skipped when empty.
      const faqList = (Array.isArray(uc.faq) ? uc.faq : []).filter((f) => f && f.q && f.a)
      setJsonLd('faq', faqList.length ? {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqList.map((f) => ({
          '@type': 'Question',
          name: String(f.q),
          acceptedAnswer: { '@type': 'Answer', text: String(f.a) },
        })),
      } : null)
    } catch (_) { /* SEO metadata must never break the page */ }

    return () => {
      setJsonLd('breadcrumb', null)
      setJsonLd('service', null)
      setJsonLd('faq', null)
      resetSeoDefaults()
    }
  }, [status, uc, slug])

  const goCart = useCallback(() => navigate('/cart'), [navigate])
  const signIn = { to: authed ? '/' : '/signin', label: authed ? 'Open console' : 'Sign in', router: true }

  // ── Not-found (graceful, inside the shell) ─────────────────────────────────
  if (status === 'notfound') {
    return (
      <div className="mkt min-h-screen bg-gray-50 text-gray-900 antialiased flex flex-col">
        <MarketingNav services={services} cartCount={seatCount} onOpenCart={goCart} signIn={signIn} />
        <section className="flex-1 flex items-center justify-center px-5 py-24">
          <div className="text-center max-w-md">
            <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-gray-400">404</p>
            <h1 className="text-3xl sm:text-4xl font-display font-black tracking-tight text-gray-900 mt-3" style={{ textWrap: 'balance' }}>
              Use case not found
            </h1>
            <p className="text-[16px] text-gray-500 mt-3 leading-relaxed">
              We couldn’t find a use case at that address. It may have been renamed or moved.
            </p>
            <a
              href="/use-cases"
              className="mt-8 inline-flex items-center justify-center gap-1.5 rounded-full bg-primary-600 text-white text-[15px] font-semibold px-6 py-3 hover:bg-primary-700 transition-all duration-200 shadow-sm hover:shadow"
            >
              <ArrowLeft size={16} /> Browse all use cases
            </a>
          </div>
        </section>
        <SiteFooter />
      </div>
    )
  }

  // ── Loading (calm skeleton, inside the shell) ──────────────────────────────
  if (status === 'loading' || !uc) {
    return (
      <div className="mkt min-h-screen bg-gray-50 text-gray-900 antialiased">
        <MarketingNav services={services} cartCount={seatCount} onOpenCart={goCart} signIn={signIn} />
        <section className="max-w-4xl mx-auto px-5 pt-20 sm:pt-28 pb-16">
          <div className="animate-pulse space-y-5">
            <div className="h-3 w-40 bg-gray-200 rounded-full" />
            <div className="h-12 w-4/5 bg-gray-200 rounded-2xl" />
            <div className="h-4 w-full bg-gray-200 rounded-full" />
            <div className="h-4 w-2/3 bg-gray-200 rounded-full" />
            <div className="h-72 w-full bg-gray-100 rounded-3xl mt-6" />
          </div>
        </section>
      </div>
    )
  }

  // ── The use-case page ──────────────────────────────────────────────────────
  const kw = uc.keyword || uc.h1 || 'Use case'
  const label = uc.keyword || titleCase(slug)
  const intro = uc.intro || uc.description || ''
  const whatItIs = Array.isArray(uc.whatItIs) ? uc.whatItIs : []
  const howItWorks = Array.isArray(uc.howItWorks) ? uc.howItWorks : []
  const useCases = Array.isArray(uc.useCases) ? uc.useCases : []
  const faq = Array.isArray(uc.faq) ? uc.faq : []
  const integrations = Array.isArray(uc.integrations) ? uc.integrations : []
  const does = Array.isArray(uc.does) ? uc.does : []
  const doesnt = Array.isArray(uc.doesnt) ? uc.doesnt : []
  const security = Array.isArray(uc.security) ? uc.security : []
  const relatedSlugs = Array.isArray(uc.related) ? uc.related : []
  const media = Array.isArray(uc.media) ? uc.media : []

  // The mapped product this scenario sells → the primary CTA target.
  const serviceSlug = uc.service || ''
  const bySlugMap = {}
  for (const s of services) bySlugMap[s.slug] = s
  const serviceRow = bySlugMap[serviceSlug] || DEFAULT_SERVICES.find((d) => d.slug === serviceSlug) || null
  const serviceName = serviceRow?.name || serviceRow?.keyword || titleCase(serviceSlug)
  const servicePrice = serviceRow?.price
  const productUrl = serviceSlug ? `/products/${serviceSlug}` : '/products'
  const ctaLabel = uc.cta?.label || `See the ${serviceName} →`

  // Resolve `related` → real rows. A slug is either a PRODUCT (→ /products/*, has a
  // price + icon) or a sibling USE-CASE (→ /use-cases/*, no price). Products win when
  // a slug matches both catalogs.
  const ucBySlug = {}
  for (const u of usecases) ucBySlug[u.slug] = u
  const related = relatedSlugs.map((rs) => {
    const prod = bySlugMap[rs] || DEFAULT_SERVICES.find((d) => d.slug === rs)
    if (prod) return { slug: rs, name: prod.name, short: prod.short, price: prod.price, icon: prod.icon, href: `/products/${rs}` }
    const u = ucBySlug[rs]
    if (u) return { slug: rs, name: u.name || u.keyword, short: u.short || u.title || '', price: null, icon: null, href: `/use-cases/${rs}` }
    return null
  }).filter(Boolean)

  // Secondary media (mirrors ProductPage): what-it-is context + optional demo band.
  const whatMedia = mediaFrom(media[0])
  const hasWhatMedia = !!(whatMedia.image || whatMedia.video)
  const demo = mediaFrom(media[1])
  const hasDemo = !!(uc.heroVideo || demo.image || demo.video)

  return (
    <div className="mkt min-h-screen bg-gray-50 text-gray-900 antialiased">
      <MarketingNav services={services} cartCount={seatCount} onOpenCart={goCart} signIn={signIn} />

      {/* ── Breadcrumb ──────────────────────────────────────────────────── */}
      <nav aria-label="Breadcrumb" className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-5 py-3 flex items-center gap-2 text-[13px] text-gray-500">
          <a href="/use-cases" className="hover:text-gray-900 transition-colors">Use cases</a>
          <span className="text-gray-300" aria-hidden="true">/</span>
          <span className="text-gray-700 font-medium">{label}</span>
        </div>
      </nav>

      {/* ── Hero ────────────────────────────────────────────────────────── */}
      <section className="bg-gray-50 border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-5 pt-14 sm:pt-16 pb-14">
          <div className="max-w-3xl">
            <Eyebrow>Use case</Eyebrow>
            <h1 className="text-4xl sm:text-5xl lg:text-[3.3rem] font-display font-black tracking-tight leading-[1.04] mt-4 text-gray-900" style={{ textWrap: 'balance' }}>
              {uc.h1 || kw}
            </h1>
            <p className="text-[17px] sm:text-lg text-gray-500 mt-5 max-w-xl leading-relaxed">{intro}</p>

            {/* Informational chip — the service this scenario runs on (NOT a cart). */}
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <a
                href={productUrl}
                className="inline-flex items-center gap-1.5 rounded-full border border-gray-300 bg-gray-100 px-4 py-1.5 hover:border-gray-400 transition-colors"
              >
                <span className="text-[13px] text-gray-500">Powered by</span>
                <span className="text-[14px] font-semibold text-gray-900">{serviceName}</span>
                {servicePrice != null && (
                  <span className="text-[13px] text-gray-400">· ${servicePrice}/mo</span>
                )}
              </a>
              <span className="text-[13px] text-gray-500">Pay only for the services you switch on.</span>
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-3">
              <a
                href={productUrl}
                className="inline-flex items-center justify-center gap-1.5 rounded-full bg-primary-600 text-white text-[15px] font-semibold px-6 py-3 hover:bg-primary-700 transition-all duration-200 shadow-sm hover:shadow"
              >
                {ctaLabel}
              </a>
              <a
                href="/"
                className="inline-flex items-center justify-center gap-1.5 rounded-full border border-gray-300 text-gray-900 text-[15px] font-semibold px-6 py-3 hover:border-gray-400 hover:bg-gray-100 transition-all duration-200"
              >
                Start free
              </a>
            </div>
          </div>

          {/* Full-width hero banner. */}
          <div className="mt-12 sm:mt-16">
            <MediaSlot
              image={uc.heroImage || ''}
              video={uc.heroVideo || ''}
              kind={uc.heroVideo ? 'video' : 'image'}
              label={`${label} — hero`}
              sublabel="Image or video · 16:9 · add in the CMS"
            />
          </div>
        </div>
      </section>

      {/* ── What it is ──────────────────────────────────────────────────── */}
      {whatItIs.length > 0 && (
        <section className="bg-gray-100 border-b border-gray-200">
          <div className="max-w-6xl mx-auto px-5 py-20 sm:py-24">
            <div className={hasWhatMedia ? 'grid lg:grid-cols-2 gap-12 lg:gap-16 items-center' : 'max-w-3xl'}>
              <div>
                <SectionHead index="WHAT IT IS" title={`Meet ${serviceName}.`} />
                <div className="mt-6 space-y-4">
                  {whatItIs.map((p, i) => (
                    <p key={i} className="text-[16px] sm:text-[17px] text-gray-600 leading-relaxed">{p}</p>
                  ))}
                </div>
              </div>
              {hasWhatMedia && (
                <MediaSlot
                  image={whatMedia.image}
                  video={whatMedia.video}
                  poster={whatMedia.poster}
                  kind={whatMedia.video ? 'video' : 'image'}
                  label={`${label} — in context`}
                />
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── Data sources / integrations ────────────────────────────────── */}
      {integrations.length > 0 && (
        <section className="bg-gray-50 border-b border-gray-200">
          <div className="max-w-6xl mx-auto px-5 py-20 sm:py-24">
            <SectionHead index="DATA SOURCES" title="Connects to the tools you already use." blurb="Wire up what you have — start with one source and add the rest as you go. There's no data warehouse to build first." />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3 mt-12">
              {integrations.map((it, i) => (
                <div key={i} className="bg-gray-100 rounded-xl border border-gray-200 px-5 py-4">
                  <p className="text-[15px] font-semibold text-gray-900">{it.name}</p>
                  {it.note && <p className="text-[13px] text-gray-500 mt-1 leading-relaxed">{it.note}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── How it works ───────────────────────────────────────────────── */}
      {howItWorks.length > 0 && (
        <section className="bg-gray-50 border-b border-gray-200">
          <div className="max-w-6xl mx-auto px-5 py-20 sm:py-24">
            <SectionHead index="HOW IT WORKS" title="From your content to live in four steps." />
            <div className="grid sm:grid-cols-2 gap-x-10 gap-y-10 mt-12">
              {howItWorks.map((step, i) => (
                <div key={i} className="flex gap-5">
                  <span className="shrink-0 w-10 h-10 rounded-full bg-primary-500/10 text-primary-600 font-display font-black flex items-center justify-center text-[15px] tabular-nums">
                    {i + 1}
                  </span>
                  <p className="text-[16px] text-gray-600 leading-relaxed pt-1.5">{step}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── What it does & doesn't ─────────────────────────────────────── */}
      {(does.length > 0 || doesnt.length > 0) && (
        <section className="bg-gray-100 border-b border-gray-200">
          <div className="max-w-6xl mx-auto px-5 py-20 sm:py-24">
            <SectionHead index="THE HONEST BOUNDARIES" title="What it does — and what it doesn't." />
            <div className="grid md:grid-cols-2 gap-5 mt-12">
              <div className="bg-gray-50 rounded-2xl border border-gray-200 p-7">
                <p className="text-[13px] font-bold uppercase tracking-[0.14em] text-primary-600 mb-4">What it does</p>
                <ul className="space-y-3">
                  {does.map((d, i) => (
                    <li key={i} className="flex gap-3 text-[15px] text-gray-700 leading-relaxed"><Check size={18} className="shrink-0 text-primary-600 mt-0.5" />{d}</li>
                  ))}
                </ul>
              </div>
              <div className="bg-gray-50 rounded-2xl border border-gray-200 p-7">
                <p className="text-[13px] font-bold uppercase tracking-[0.14em] text-gray-400 mb-4">What it doesn't do</p>
                <ul className="space-y-3">
                  {doesnt.map((d, i) => (
                    <li key={i} className="flex gap-3 text-[15px] text-gray-500 leading-relaxed"><Minus size={18} className="shrink-0 text-gray-400 mt-0.5" />{d}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* ── See it in action (dark demo band) — only when a demo exists ─── */}
      {hasDemo && (
      <section className="bg-[#0c0e13] border-b border-gray-200">
        <div className="max-w-6xl mx-auto px-5 py-20 sm:py-28 text-center">
          <p className="inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.14em] text-primary-400 justify-center">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-500" aria-hidden="true" />See it in action
          </p>
          <h2 className="text-3xl sm:text-5xl font-display font-black tracking-tight leading-[1.05] mt-4 text-white" style={{ textWrap: 'balance' }}>
            Watch {serviceName} at work.
          </h2>
          <p className="text-[16px] sm:text-lg text-gray-300/80 mt-4 max-w-2xl mx-auto leading-relaxed">
            A short walk-through, end to end — from bringing your content to the first real result.
          </p>
          <div className="mt-12">
            <MediaSlot
              image={demo.image}
              video={uc.heroVideo || demo.video}
              poster={uc.heroImage || ''}
              kind={(uc.heroVideo || demo.video) ? 'video' : 'image'}
              label={`${label} demo`}
              onDark
            />
          </div>
        </div>
      </section>
      )}

      {/* ── Use cases (cards) ──────────────────────────────────────────── */}
      {useCases.length > 0 && (
        <section className="bg-gray-100 border-b border-gray-200">
          <div className="max-w-6xl mx-auto px-5 py-20 sm:py-24">
            <SectionHead index="WHERE IT FITS" title="Where this earns its keep." />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
              {useCases.map((item, i) => (
                <div key={i} className="bg-gray-50 rounded-2xl border border-gray-200 p-6">
                  <span className="inline-flex w-8 h-8 rounded-lg bg-primary-500/10 text-primary-600 font-display font-black items-center justify-center text-[13px] tabular-nums">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <p className="text-[15px] text-gray-600 mt-4 leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Security, privacy & human review ───────────────────────────── */}
      {security.length > 0 && (
        <section className="bg-gray-50 border-b border-gray-200">
          <div className="max-w-6xl mx-auto px-5 py-20 sm:py-24">
            <SectionHead index="SECURITY & CONTROL" title="Your data, your approval." />
            <div className="grid sm:grid-cols-2 gap-x-10 gap-y-8 mt-12">
              {security.map((s, i) => (
                <div key={i} className="flex gap-4">
                  <span className="shrink-0 w-9 h-9 rounded-lg bg-primary-500/10 text-primary-600 flex items-center justify-center"><Check size={17} /></span>
                  <div>
                    <p className="text-[16px] font-bold text-gray-900 tracking-tight">{s.t}</p>
                    <p className="text-[14px] text-gray-500 mt-1 leading-relaxed">{s.d}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── FAQ (accordion) ────────────────────────────────────────────── */}
      {faq.length > 0 && (
        <section className="bg-gray-50 border-b border-gray-200">
          <div className="max-w-3xl mx-auto px-5 py-20 sm:py-24">
            <SectionHead index="FAQ" title="Frequently asked." center />
            <div className="mt-10 divide-y divide-gray-200 border-y border-gray-200">
              {faq.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} />)}
            </div>
          </div>
        </section>
      )}

      {/* ── Related (mapped product + sibling use-case) ────────────────── */}
      {related.length > 0 && (
        <section className="bg-gray-100 border-b border-gray-200">
          <div className="max-w-6xl mx-auto px-5 py-20 sm:py-24">
            <SectionHead index="EXPLORE NEXT" title="The product behind it — and a related scenario." blurb="This scenario runs on a real ReRight service. See the product it maps to, or a related use case." />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-12">
              {related.map((r) => {
                const Icon = r.icon
                return (
                  <a
                    key={r.slug}
                    href={r.href}
                    className="group relative flex flex-col text-left bg-gray-50 rounded-2xl border border-gray-200 p-6 hover:border-gray-300 hover:shadow-[0_16px_40px_-24px_rgba(15,23,42,0.35)] hover:-translate-y-0.5 transition-all duration-200"
                  >
                    <div className="flex items-start justify-between">
                      <div className="w-11 h-11 rounded-xl bg-primary-500/10 flex items-center justify-center group-hover:bg-primary-500/15 transition-colors">
                        {Icon
                          ? <Icon className="text-primary-600" size={21} />
                          : <span className="text-primary-600 font-display font-black text-lg">{(r.name || '?').charAt(0)}</span>}
                      </div>
                      {r.price != null && (
                        <span className="text-[15px] font-semibold text-gray-900 tabular-nums">${r.price}<span className="text-gray-400 font-normal text-[13px]">/mo</span></span>
                      )}
                    </div>
                    <h3 className="font-bold text-gray-900 mt-4 text-[17px] tracking-tight">{r.name}</h3>
                    <p className="text-[13.5px] text-gray-500 mt-1.5 leading-relaxed flex-1">{r.short}</p>
                    <span className="mt-4 text-[13px] font-semibold inline-flex items-center gap-1 text-primary-600">
                      View <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </a>
                )
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── Final CTA band ─────────────────────────────────────────────── */}
      <section className="bg-gray-50">
        <div className="max-w-6xl mx-auto px-5 py-24 text-center">
          <h2 className="text-3xl sm:text-5xl font-display font-black tracking-tight text-gray-900" style={{ textWrap: 'balance' }}>
            Ready? Switch on {serviceName}.
          </h2>
          <p className="text-[17px] sm:text-xl text-gray-500 mt-4 max-w-2xl mx-auto">
            {servicePrice != null ? `$${servicePrice}/mo. ` : ''}Add it on its own or alongside the rest — you only pay for what you turn on.
          </p>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <a
              href={productUrl}
              className="inline-flex items-center justify-center gap-1.5 rounded-full bg-primary-600 text-white text-[15px] font-semibold px-6 py-3 hover:bg-primary-700 transition-all duration-200 shadow-sm hover:shadow"
            >
              {ctaLabel}
            </a>
            <a
              href="/use-cases"
              className="inline-flex items-center justify-center gap-1.5 rounded-full border border-gray-300 text-gray-900 text-[15px] font-semibold px-6 py-3 hover:border-gray-400 hover:bg-gray-100 transition-all duration-200"
            >
              Browse all use cases
            </a>
          </div>
        </div>
      </section>

      <SiteFooter />
    </div>
  )
}

// ── Helpers (mirror ProductPage) ─────────────────────────────────────────────
function setMetaDescription(text) {
  if (typeof document === 'undefined') return
  let tag = document.querySelector('meta[name="description"]')
  if (!tag) {
    tag = document.createElement('meta')
    tag.setAttribute('name', 'description')
    document.head.appendChild(tag)
  }
  tag.setAttribute('content', String(text || ''))
}

// ── Per-page SEO helpers (headless, idempotent, crash-proof) ─────────────────
const SITE_ORIGIN = 'https://ai.reright.io'

// Homepage defaults captured ONCE (from index.html, before this page overwrites
// anything), so navigating away restores the site-wide <head>.
let _seoDefaults = null
function captureSeoDefaults() {
  if (_seoDefaults || typeof document === 'undefined') return _seoDefaults
  // Fixed homepage <head> — hardcoded, NOT read from the DOM (a prerendered page
  // ships with its own head; reading the DOM would capture that as the default).
  const HOME_DESC = 'Turn your website, docs and systems into AI services — a support chatbot, a voice receptionist, a private RAG API, or a marketing brain. Each answers only from your own sources, verified before it speaks, in 95+ languages.'
  const HOME_TITLE = 'ReRight — AI that answers, sells, and grows, built on your data'
  _seoDefaults = {
    title: HOME_TITLE,
    canonical: `${SITE_ORIGIN}/`,
    description: HOME_DESC,
    ogType: 'website',
    ogTitle: HOME_TITLE,
    ogDescription: HOME_DESC,
    ogUrl: `${SITE_ORIGIN}/`,
    twTitle: HOME_TITLE,
    twDescription: HOME_DESC,
  }
  return _seoDefaults
}
function resetSeoDefaults() {
  if (!_seoDefaults) return
  document.title = _seoDefaults.title
  setMetaDescription(_seoDefaults.description)
  setCanonical(_seoDefaults.canonical)
  setMetaByProperty('og:type', _seoDefaults.ogType)
  setMetaByProperty('og:title', _seoDefaults.ogTitle)
  setMetaByProperty('og:description', _seoDefaults.ogDescription)
  setMetaByProperty('og:url', _seoDefaults.ogUrl)
  setMetaByName('twitter:title', _seoDefaults.twTitle)
  setMetaByName('twitter:description', _seoDefaults.twDescription)
}

function setCanonical(href) {
  if (typeof document === 'undefined') return
  let link = document.head.querySelector('link[rel="canonical"]')
  if (!link) { link = document.createElement('link'); link.setAttribute('rel', 'canonical'); document.head.appendChild(link) }
  link.setAttribute('href', String(href || ''))
}

function setMetaByProperty(property, content) {
  if (typeof document === 'undefined') return
  let tag = document.head.querySelector(`meta[property="${property}"]`)
  if (!tag) { tag = document.createElement('meta'); tag.setAttribute('property', property); document.head.appendChild(tag) }
  tag.setAttribute('content', String(content || ''))
}

function setMetaByName(name, content) {
  if (typeof document === 'undefined') return
  let tag = document.head.querySelector(`meta[name="${name}"]`)
  if (!tag) { tag = document.createElement('meta'); tag.setAttribute('name', name); document.head.appendChild(tag) }
  tag.setAttribute('content', String(content || ''))
}

// Add / replace / remove a keyed JSON-LD <script> in <head>. obj === null removes
// it, so every route cleans up its own structured data on navigation.
function setJsonLd(key, obj) {
  if (typeof document === 'undefined') return
  const id = `rr-jsonld-${key}`
  let el = document.getElementById(id)
  if (obj == null) { if (el) el.remove(); return }
  if (!el) {
    el = document.createElement('script')
    el.type = 'application/ld+json'
    el.id = id
    document.head.appendChild(el)
  }
  try { el.textContent = JSON.stringify(obj) } catch (_) { el.remove() }
}

const isVideoUrl = (u) => typeof u === 'string' && /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(u)
function mediaFrom(entry) {
  if (!entry) return { image: '', video: '', poster: '' }
  if (typeof entry === 'string') return isVideoUrl(entry) ? { video: entry, image: '', poster: '' } : { image: entry, video: '', poster: '' }
  const url = entry.url || entry.src || ''
  const video = entry.video || entry.mp4 || (isVideoUrl(url) ? url : '') || ''
  const image = entry.image || (!video ? url : '') || entry.poster || ''
  return { video: video || '', image: image || '', poster: entry.poster || '' }
}

function Eyebrow({ children, center }) {
  return (
    <p className={`inline-flex items-center gap-1.5 text-[12px] font-bold uppercase tracking-[0.14em] text-primary-600 ${center ? 'justify-center' : ''}`}>
      <span className="w-1.5 h-1.5 rounded-full bg-primary-600" aria-hidden="true" />{children}
    </p>
  )
}

function SectionHead({ index, title, blurb, center }) {
  return (
    <div className={center ? 'text-center' : ''}>
      <p className="text-[12px] font-bold uppercase tracking-[0.16em] text-gray-400">{index}</p>
      <h2 className="text-3xl sm:text-4xl font-display font-black tracking-tight text-gray-900 mt-3" style={{ textWrap: 'balance' }}>{title}</h2>
      {blurb && <p className={`text-[16px] text-gray-500 mt-3 leading-relaxed ${center ? 'max-w-2xl mx-auto' : 'max-w-2xl'}`}>{blurb}</p>}
    </div>
  )
}

function FaqItem({ q, a }) {
  return (
    <details className="group py-4">
      <summary className="flex items-center justify-between cursor-pointer list-none">
        <span className="text-[16px] font-semibold text-gray-900 pr-6">{q}</span>
        <span className="shrink-0 w-6 h-6 rounded-full border border-gray-300 flex items-center justify-center text-gray-500 group-open:bg-primary-600 group-open:border-primary-600 group-open:text-white transition-colors">
          <Plus size={14} className="group-open:hidden" />
          <Minus size={14} className="hidden group-open:block" />
        </span>
      </summary>
      <p className="text-[14.5px] text-gray-500 leading-relaxed mt-3 pr-8">{a}</p>
    </details>
  )
}
